import React, { FC, memo, ReactNode, useCallback, useRef } from 'react';
import styles from './content-slider.module.css';
import { Button } from '../button/button.component';
import ChevronLeft from '$icons/chevron-left.svg';
import ChevronRight from '$icons/chevron-right.svg';
import { Typography } from '../typography/typography.component';
import { Link } from '~/lib/data-contract';
import { CMSLink } from '../cms-link/cms-link.component';
import clsx from 'clsx';
import { useMedia } from 'react-use';
import { queries } from '~/theme/breakpoints';

type Props = {
    children?: ReactNode[];
    className?: string;
    headline?: string;
    link?: string | Link;
    hasGutter?: boolean;
    headlineVariant?: 'display1' | 'display2' | 'display3' | 'display4' | 'display5' | 'display6';
    basket?: boolean;
    scrollAmount?: number;
};

const ContentSlider: FC<Props> = memo((props) => {
    const {
        children,
        className,
        headline,
        link,
        headlineVariant = 'display2',
        scrollAmount = 900,
    } = props;
    const isMobile = !useMedia(queries.md, false);

    const sliderRef = useRef<HTMLUListElement>(null);
    const startPos = useRef(0);
    const scrollPos = useRef(0);
    const isDragging = useRef(false);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!sliderRef.current) return;
        e.preventDefault();
        const dx = e.clientX - startPos.current;
        const newScrollPos = scrollPos.current - dx;
        sliderRef.current.scrollLeft = newScrollPos;
        sliderRef.current.classList.add(styles.dragging);
    }, []);

    const handleMouseUp = useCallback(() => {
        isDragging.current = false;
        if (sliderRef.current) {
            sliderRef.current.classList.remove(styles.dragging);
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!sliderRef.current) return;
            isDragging.current = true;
            startPos.current = e.clientX;
            scrollPos.current = sliderRef.current.scrollLeft;

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [handleMouseMove, handleMouseUp]
    );

    if (!children?.length) {
        return null;
    }

    const handleScroll = (e: React.MouseEvent, position: 'left' | 'right') => {
        e.stopPropagation();
        if (sliderRef.current) {
            sliderRef.current.scroll({
                left:
                    position === 'left'
                        ? sliderRef.current.scrollLeft - scrollAmount
                        : sliderRef.current.scrollLeft + scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    return (
        <div className={clsx(styles.root, className, { [styles.basket]: props.basket })}>
            <div
                className={clsx(
                    styles.headerWrapper,
                    { [styles.gutter]: props.hasGutter },
                    { [styles.slider]: !props.basket }
                )}
            >
                {headline && <Typography variant={headlineVariant}>{headline}</Typography>}
                {link && (
                    <CMSLink
                        href={typeof link === 'string' ? link : link?.url}
                        className={styles.link}
                    >
                        {typeof link === 'string' ? link : link?.text}
                    </CMSLink>
                )}

                <div className={styles.pagination}>
                    <Button
                        className={styles.navButton}
                        variant="tertiary"
                        onClick={(e) => handleScroll(e, 'left')}
                    >
                        <ChevronLeft />
                    </Button>
                    <Button
                        onClick={(e) => handleScroll(e, 'right')}
                        className={styles.navButton}
                        variant="tertiary"
                    >
                        <ChevronRight />
                    </Button>
                </div>
            </div>

            <ul
                ref={sliderRef}
                onMouseDown={isMobile ? undefined : handleMouseDown}
                className={clsx(styles.sliderRoot, { [styles.gutter]: props.hasGutter })}
            >
                {children.map((child, index) => (
                    <li
                        key={index}
                        className={clsx({
                            [styles.sliderItem]: !props.basket,
                        })}
                    >
                        {child}
                    </li>
                ))}
            </ul>
        </div>
    );
});

export default ContentSlider;
